import { useEffect, useState, type DragEvent } from 'react'
import { Icon } from '../../ui/Icon'
import { Thumb } from '../../ui/Thumb'
import { failureText } from '../../ui/Load'
import { COVER_MAX_BYTES, useRemoveCover, useUploadCover, type Category } from './api'

export interface CategoryCoverProps {
  category: Category
  canUpdate: boolean
  onToast: (message: string) => void
  /**
   * Новая категория: загружать файл ещё некуда — он ждёт здесь, с превью,
   * и уходит на сервер сразу после создания категории.
   */
  local?: { file: File | null; onChange: (file: File | null) => void }
}

/** Адрес превью для выбранного файла; убирается вместе с файлом. */
function usePreview(file: File | null | undefined): string {
  const [url, setUrl] = useState('')
  useEffect(() => {
    // В тестовом окружении (jsdom) object URL нет — тогда без превью.
    if (!file || typeof URL.createObjectURL !== 'function') {
      setUrl('')
      return
    }
    const next = URL.createObjectURL(file)
    setUrl(next)
    return () => {
      URL.revokeObjectURL(next)
    }
  }, [file])
  return url
}

/**
 * Обложка категории — картинка на плитке в приложении. Файл можно перетащить
 * на блок или выбрать; обложка одна, поэтому из нескольких файлов берём
 * первый. Размер проверяем ещё здесь: 5 МБ сервер всё равно отклонит, а ждать
 * отказа после долгой выгрузки незачем. Тип файла сервер определяет сам, по
 * байтам. Загрузка не ждёт кнопки «Сохранить»: файл — не черновик.
 */
export function CategoryCover({ category, canUpdate, onToast, local }: CategoryCoverProps) {
  const upload = useUploadCover()
  const remove = useRemoveCover()
  const [error, setError] = useState('')
  // Сколько вложенных элементов под курсором с файлом: dragleave приходит и
  // при переходе на дочерний элемент, счётчик не даёт подсветке мигать.
  const [depth, setDepth] = useState(0)

  const preview = usePreview(local?.file)
  const busy = upload.isPending || remove.isPending
  const hasCover = local ? local.file !== null : category.coverUrl !== ''
  const coverUrl = local ? preview : category.coverUrl

  function send(file: File | undefined) {
    // Окно выбора закрыли, ничего не выбрав, или бросили не файл — делать нечего.
    if (!file) return
    if (file.size > COVER_MAX_BYTES) {
      setError('Файл больше 5 МБ')
      return
    }
    setError('')
    if (local) {
      local.onChange(file)
      return
    }
    upload.mutate(
      { id: category.id, file },
      {
        onSuccess: () => {
          onToast(hasCover ? 'Обложка заменена' : 'Обложка загружена')
        },
        onError: (failure) => {
          onToast(failureText(failure))
        },
      },
    )
  }

  function drop() {
    if (local) {
      local.onChange(null)
      return
    }
    remove.mutate(category.id, {
      onSuccess: () => {
        onToast('Обложка убрана')
      },
      onError: (failure) => {
        onToast(failureText(failure))
      },
    })
  }

  const dropZone = canUpdate
    ? {
        onDragEnter: (event: DragEvent) => {
          event.preventDefault()
          setDepth((d) => d + 1)
        },
        onDragOver: (event: DragEvent) => {
          event.preventDefault()
        },
        onDragLeave: () => {
          setDepth((d) => d - 1)
        },
        onDrop: (event: DragEvent) => {
          event.preventDefault()
          setDepth(0)
          // Пока идёт прежняя загрузка или удаление, второй файл не шлём:
          // ответы пришли бы в любом порядке.
          if (!busy) send(event.dataTransfer.files[0])
        },
      }
    : {}

  const picker = (
    <input
      type="file"
      className="visually-hidden"
      accept="image/jpeg,image/png,image/webp"
      aria-label={hasCover ? 'Заменить обложку' : 'Загрузить обложку'}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? 'c-cover-error' : undefined}
      disabled={busy}
      onChange={(e) => {
        const file = e.target.files?.[0]
        // Поле сбрасываем сразу: иначе тот же файл второй раз не выбрать.
        e.target.value = ''
        send(file)
      }}
    />
  )

  return (
    <section
      className="ui-card ui-drop-card"
      aria-labelledby="c-cover"
      aria-busy={busy}
      data-drop={depth > 0 || undefined}
      {...dropZone}
    >
      <div className="ui-card-head">
        <h3 id="c-cover">Обложка</h3>
        {upload.isPending ? <span className="aside">Загружаем…</span> : null}
        {local?.file ? <span className="aside">загрузится вместе с категорией</span> : null}
      </div>
      <div className="ui-card-body cat-cover-body">
        {hasCover ? (
          <div className="cat-cover-grid">
            <div className="cat-cover-img">
              <Thumb
                photos={coverUrl ? [coverUrl] : []}
                alt={`Обложка категории «${category.name || 'новая'}»`}
                fluid
              />
              {canUpdate ? (
                <button
                  type="button"
                  className="ui-iconbtn cat-cover-remove"
                  aria-label="Убрать обложку"
                  data-tip="Убрать обложку"
                  disabled={busy}
                  aria-busy={remove.isPending}
                  onClick={drop}
                >
                  <Icon name="i-trash" />
                </button>
              ) : null}
            </div>
            {canUpdate ? (
              <label className="ui-drop ui-drop-tile">
                <Icon name="i-img" />
                <span>Заменить</span>
                {picker}
              </label>
            ) : null}
          </div>
        ) : null}

        {!hasCover && canUpdate ? (
          <label className="ui-drop">
            <Icon name="i-img" />
            <b>Перетащите обложку сюда</b>
            <span>
              или <u>выберите файл</u> · JPEG, PNG, WebP до 5 МБ
            </span>
            {picker}
          </label>
        ) : null}

        {!hasCover && !canUpdate ? (
          <p className="ui-muted-line">Обложки нет — плитка в приложении без картинки</p>
        ) : null}

        {error ? (
          <span className="ui-error" id="c-cover-error" role="alert">
            <Icon name="i-alert" />
            {error}
          </span>
        ) : null}
      </div>

      {depth > 0 ? (
        <div className="ui-drop-over" aria-hidden="true">
          <Icon name="i-img" />
          Отпустите, чтобы загрузить
        </div>
      ) : null}
    </section>
  )
}
